import constants from '@core/utils/constants';
import { bus } from '@core/events/eventSystem';
import { getStore } from '@core/store';
import { registrationSerializer } from '../registration/registrationSerializer';

const passwordResetRequestIdPrefix = 'password-reset-';
let currentPasswordResetEmail = null;

// Démarrer le processus de réinitialisation de mot de passe
function initiatePasswordReset() {
  const requestId = `${passwordResetRequestIdPrefix}${Date.now()}`;

  bus.emit(constants.EVENTS.OTP.SEND_OTP, {
    requestId,
    channel: 'email',
    showContactInput: true,
    validate: true,
    checkUser: true
  });
}

// Écouter la validation de l'OTP
bus.on(constants.EVENTS.OTP.OTP_VALIDATED, (data) => {
  if (data?.requestId && data.requestId.startsWith(passwordResetRequestIdPrefix)) {
    currentPasswordResetEmail = data.email;
    bus.emit(constants.EVENTS.PASSWORD_RESET.SHOW_NEW_PASSWORD_INPUT, {
      email: data.email
    });
  }
});

// Listener pour SHOW_NEW_PASSWORD_INPUT
bus.on(constants.EVENTS.PASSWORD_RESET.SHOW_NEW_PASSWORD_INPUT, (data) => {
  if (data?.email) {
    currentPasswordResetEmail = data.email;
  }
});

// Listener pour PASSWORD_RESET.REQUESTED
bus.on(constants.EVENTS.PASSWORD_RESET.REQUESTED, () => {
  initiatePasswordReset();
});

// Listener pour soumettre le nouveau mot de passe
bus.on(constants.EVENTS.PASSWORD_RESET.SUBMIT_NEW_PASSWORD, async (data) => {
  const { password } = data || {};

  if (!password || password.length < 6) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Mot de passe invalide',
      body: 'Le mot de passe doit contenir au moins 6 caractères'
    });
    bus.emit(constants.EVENTS.PASSWORD_RESET.RESET_ERROR, {
      error: 'Mot de passe trop court'
    });
    return;
  }

  if (!currentPasswordResetEmail) {
    bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
      title: 'Erreur',
      body: 'Adresse email introuvable pour la réinitialisation'
    });
    bus.emit(constants.EVENTS.PASSWORD_RESET.RESET_ERROR, {
      error: 'Email non trouvé'
    });
    return;
  }

  const passwordResetStore = getStore('passwordReset');
  const isMigrationMode = passwordResetStore?.state?.isMigration || false;
  const legacyUserData = passwordResetStore?.state?.legacyData || null;

  // Si mode migration, créer le compte d'abord
  if (isMigrationMode && legacyUserData) {
    const reqData = registrationSerializer.serialize({
      email: currentPasswordResetEmail,
      password: password,
      firstName: legacyUserData.firstName || 'Utilisateur',
      lastName: legacyUserData.lastName || 'Migré',
      phonePrefix: '229',
      phoneNumber: '',
      acceptMoreThan18: true,
      termOfUse: true
    });

    const requestId = `migration-register-${Date.now()}`;

    bus.emit(constants.EVENTS.NETWORK.REQUEST, {
      id: requestId,
      url: `${constants.ENDPOINTS.USER.REGISTER}`,
      method: 'POST',
      data: reqData
    });

    const handler = (res) => {
      if (res.id === requestId) {
        bus.off(constants.EVENTS.NETWORK.RESPONSE, handler);

        const parsed = registrationSerializer.deserialize(res.data || res.error);

        if (parsed.success) {
          bus.emit(constants.EVENTS.USER.REGISTER_SUCCESS, parsed.user);
          bus.emit(constants.EVENTS.PASSWORD_RESET.RESET_SUCCESS);
          bus.emit(constants.EVENTS.LEGACY_MIGRATION.MIGRATION_COMPLETE);

          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'Compte créé',
            body: 'Votre compte a été initialisé avec succès'
          });
          currentPasswordResetEmail = null;
        } else if (parsed.alreadyExists) {
          bus.emit(constants.EVENTS.USER.LOGIN, {
            email: currentPasswordResetEmail,
            password: password
          });
        } else {
          bus.emit(constants.EVENTS.LEGACY_MIGRATION.MIGRATION_FAILED);
          bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
            title: 'Erreur',
            body: parsed.message || 'Impossible de créer le compte'
          });
          bus.emit(constants.EVENTS.PASSWORD_RESET.RESET_ERROR, {
            error: parsed.message
          });
        }
      }
    };
    bus.on(constants.EVENTS.NETWORK.RESPONSE, handler);
    return;
  }

  // Mode normal : mise à jour directe du mot de passe
  bus.emit(constants.EVENTS.LOADING.START);
  const updateReqId = `password-reset-update-${Date.now()}`;

  bus.emit(constants.EVENTS.NETWORK.REQUEST, {
    id: updateReqId,
    url: `${constants.ENDPOINTS.USER.UPDATE}`,
    method: 'POST',
    data: {
      email: currentPasswordResetEmail,
      password: password
    }
  });

  const updateHandler = (res) => {
    if (res.id === updateReqId) {
      bus.off(constants.EVENTS.NETWORK.RESPONSE, updateHandler);
      bus.emit(constants.EVENTS.LOADING.FINISH);

      const resData = res.data || res.error;
      const isSuccess = res.success && (resData?.statusCode === 200 || resData?.success);

      if (isSuccess) {
        bus.emit(constants.EVENTS.PASSWORD_RESET.RESET_SUCCESS);
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Mot de passe modifié',
          body: 'Votre mot de passe a été réinitialisé avec succès.'
        });
        currentPasswordResetEmail = null;
      } else {
        const errorMsg = resData?.message || resData?.error || 'Échec de la réinitialisation du mot de passe';
        bus.emit(constants.EVENTS.PASSWORD_RESET.RESET_ERROR, {
          error: errorMsg
        });
        bus.emit(constants.EVENTS.TOAST_NOTIFICATION.SHOW, {
          title: 'Erreur',
          body: errorMsg
        });
      }
    }
  };

  bus.on(constants.EVENTS.NETWORK.RESPONSE, updateHandler);
});

// Listener pour annuler
bus.on(constants.EVENTS.PASSWORD_RESET.CANCEL, () => {
  currentPasswordResetEmail = null;
});
